/**
 * Speed view of the Scroll speed property: effective speed and distance travelled (W2-04b curve-editor;
 * DESIGN.md §4.7 "Speed view", §5.6.7, §6.11; ARCHITECTURE.md §7.7; rpe-format.md F§7).
 *
 * What moves the notes is the line's floor position H(t) produced by the active speed integrator (legacy, or
 * Phira's 1.7 integrator in its Modern / Legacy-170 sub-mode), not the raw keyframe values: a legacy-mode eased
 * segment is baked into linear pieces on export, an rpe170 `easingType 0` segment is a step, the Legacy-170
 * sub-mode plays k·f'(x)+b. So the speed view draws
 *   - the EFFECTIVE speed dH/dt (`engine.sampleEffectiveSpeed`, RPE speed units) as the main curve,
 *   - the keyframe values as handles on a thin guide curve (the editable data, drawn by curveEditor.ts),
 *   - the DISTANCE TRAVELLED (floor position, `engine.sampleFloor` × 450 RPE-y px per height unit) on its own
 *     horizontal scale, dashed, with its ticks along the bottom edge,
 * and the feature layer shows "Notes cross the screen in N s" at the playhead (`crossScreenSeconds`).
 *
 * Pure render code: samplers are injected (engine queries curried by the feature layer) and cached in the
 * timeline's per-track curve cache, keyed by the engine revision.
 */

import { RPE_Y_PER_HEIGHT, Units } from '../../../core/model/units';
import type { TimeAxis } from '../../types';
import { curveValueRange, type BeatSampler, type CurveCache, type SampledCurve } from '../curveCache';
import { canvasFont } from '../theme';
import {
  createValueAxis,
  niceTicks,
  strokeWholeCurve,
  type CurveLaneView,
  type CurveTheme,
  type SpeedLaneOverlay,
  type ValueRange,
} from './curveEditor';

/** Height of the distance-label band at the bottom of a speed lane. */
export const DISTANCE_BAND_PX = 14;

export interface SpeedSamplers {
  /** Effective speed (dH/dt) in RPE speed units at evenly spaced beats. */
  readonly effective: BeatSampler;
  /** Floor position H (Phira height units) at evenly spaced beats. */
  readonly floor: BeatSampler;
}

export interface SpeedViewRequest {
  /** Stable key of the line, e.g. `speed:12` (cache key prefix). */
  readonly key: string;
  readonly revision: number;
  readonly beat0: number;
  readonly beat1: number;
  readonly pxPerBeat: number;
  readonly pxStep?: number;
}

/** Distance in RPE-y px from a floor position in Phira height units (1 unit = half the 900 px screen). */
export function floorToRpePx(h: number): number {
  return h * RPE_Y_PER_HEIGHT;
}

/** A floor sampler converted to RPE-y px (NaN kept). */
export function distanceSampler(floor: BeatSampler): BeatSampler {
  return (beat0, beat1, out) => {
    floor(beat0, beat1, out);
    for (let i = 0; i < out.length; i++) out[i] = floorToRpePx(out[i] as number);
  };
}

/** Samples the effective speed and the distance travelled over the visible range, through the curve cache. */
export function sampleSpeedView(
  cache: CurveCache,
  req: SpeedViewRequest,
  samplers: SpeedSamplers,
): { readonly effective: SampledCurve; readonly distance: SampledCurve } {
  const base = {
    revision: req.revision,
    beat0: req.beat0,
    beat1: req.beat1,
    pxPerBeat: req.pxPerBeat,
    ...(req.pxStep === undefined ? {} : { pxStep: req.pxStep }),
  };
  const effective = cache.sample({ ...base, track: `${req.key}:effective` }, samplers.effective);
  const distance = cache.sample({ ...base, track: `${req.key}:distance` }, distanceSampler(samplers.floor));
  return { effective, distance };
}

/** Range of the distance axis over the visible beats (padded; null when nothing is finite). */
export function distanceRangeOf(distance: SampledCurve, beatLo: number, beatHi: number): ValueRange | null {
  const r = curveValueRange(distance, beatLo, beatHi);
  if (r === null) return null;
  let [lo, hi] = r;
  if (hi - lo < 1) {
    lo -= 0.5;
    hi += 0.5;
  }
  const pad = (hi - lo) * 0.05;
  return { lo: lo - pad, hi: hi + pad };
}

/** Seconds a note needs to cross the 900 RPE-y px screen at a speed (Infinity when the notes do not move up). */
export function crossScreenSeconds(speed: number): number {
  return Units.crossScreenSec(speed);
}

/** Seconds rounded for display: 2 decimals below 10 s, 1 below 100 s, else whole; null for "never". */
export function roundCrossSeconds(sec: number): number | null {
  if (!Number.isFinite(sec) || sec <= 0) return null;
  if (sec < 10) return Math.round(sec * 100) / 100;
  if (sec < 100) return Math.round(sec * 10) / 10;
  return Math.round(sec);
}

/** Value of a sampled curve at a beat (linear between samples), NaN outside it. */
export function curveValueAt(curve: SampledCurve, beat: number): number {
  const f = (beat - curve.beat0) / curve.step;
  const i = Math.floor(f);
  const a = curve.values[i];
  const b = curve.values[i + 1];
  if (a === undefined) return Number.NaN;
  if (b === undefined || !Number.isFinite(b)) return a;
  if (!Number.isFinite(a)) return b;
  return a + (b - a) * (f - i);
}

/**
 * Draws a speed lane's effective speed (main curve, on the lane's value axis) and its distance-travelled curve
 * (dashed, own axis, ticks along the bottom band). Called by drawCurveContent before the keys.
 */
export function drawSpeedOverlay(
  ctx: CanvasRenderingContext2D,
  lane: CurveLaneView,
  axis: TimeAxis,
  theme: CurveTheme,
  distanceLabel: (v: number) => string,
): void {
  const speed: SpeedLaneOverlay | null = lane.speed;
  if (speed === null) return;
  const h = axis.heightPx;
  if (speed.distance !== null && speed.distanceRange !== null) {
    const da = createValueAxis(speed.distanceRange, lane.axis.x0, lane.axis.x1);
    const distance = speed.distance;
    ctx.save();
    ctx.strokeStyle = theme.textMuted;
    ctx.globalAlpha = 0.7;
    ctx.lineWidth = 1.25;
    ctx.setLineDash([7, 4]);
    strokeWholeCurve(ctx, distance, axis, da.valueToX);
    ctx.setLineDash([]);
    // Distance ticks along the bottom edge.
    ctx.globalAlpha = 1;
    ctx.fillStyle = theme.gutterBackground;
    ctx.fillRect(lane.x0, h - DISTANCE_BAND_PX, lane.width, DISTANCE_BAND_PX);
    const { ticks } = niceTicks(da.lo, da.hi, Math.max(2, Math.floor((da.x1 - da.x0) / 80)));
    ctx.font = canvasFont(theme, 9, 'mono');
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = theme.textMuted;
    let lastRight = Number.NEGATIVE_INFINITY;
    for (const v of ticks) {
      const x = da.valueToX(v);
      const text = distanceLabel(v);
      const m = ctx.measureText(text).width;
      const w = Number.isFinite(m) ? m : text.length * 5.5;
      if (x - w / 2 < lastRight + 4) continue;
      ctx.fillText(text, x, h - DISTANCE_BAND_PX / 2);
      lastRight = x + w / 2;
      ctx.fillRect(Math.round(x), h - DISTANCE_BAND_PX, 1, 3);
    }
    ctx.restore();
  }
  if (speed.effective !== null) {
    const effective = speed.effective;
    ctx.save();
    ctx.strokeStyle = theme.curve;
    ctx.lineWidth = 2.25;
    ctx.lineJoin = 'round';
    strokeWholeCurve(ctx, effective, axis, lane.axis.valueToX);
    ctx.restore();
  }
}
